import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY1,
  FORTA_GRAY2,
  FORTA_SUCCESS,
} from '../../../utils/colors';

import type { SxProps, Theme } from '@mui/material';
import type { ScrollState } from './types';

export const INITIAL_SCROLL_STATE = {
  canScrollLeft: false,
  canScrollRight: false,
  hasHiddenLeftError: false,
  hasHiddenRightError: false,
} satisfies ScrollState;

export const BUTTON_MENU_ERROR_BUTTON_SELECTOR = 'button[aria-invalid="true"]';
export const BUTTON_MENU_ACTIVE_BUTTON_SELECTOR = 'button[aria-current="page"]';
export const BUTTON_MENU_SCROLL_EDGE_TOLERANCE = 1;
export const BUTTON_MENU_BUTTON_GAP_PX = 12;
const BUTTON_MENU_BUTTON_MOBILE_GAP_PX = 8;
const BUTTON_MENU_BUTTON_SHADOW = '0 2px 4px rgba(0, 0, 0, 0.40)';
const BUTTON_MENU_BUTTON_HOVER_SHADOW = '0 4px 6px rgba(0, 0, 0, 0.24)';
const BUTTON_MENU_STATUS_INDICATOR_WIDTH = '90px';
const BUTTON_MENU_STATUS_INDICATOR_MOBILE_WIDTH = '75px';
const BUTTON_MENU_STATUS_INDICATOR_HEIGHT = 3;
const BUTTON_MENU_STATUS_INDICATOR_MOBILE_HEIGHT = 2;
const BUTTON_MENU_STATUS_INDICATOR_BORDER_RADIUS = 1;
const BUTTON_MENU_STATUS_INDICATOR_SELECTOR = '[data-status-indicator]';
const BUTTON_MENU_BUTTON_MOBILE_PADDING_X = 2;
const BUTTON_MENU_BUTTON_PADDING = 1;
const BUTTON_MENU_BUTTON_MOBILE_PADDING_Y = 0.75;
export const BUTTON_MENU_BUTTON_WIDTH = 100;
const BUTTON_MENU_BUTTON_MOBILE_WIDTH = 85;
const BUTTON_MENU_BUTTON_FONT_SIZE = 12;
const BUTTON_MENU_BUTTON_MOBILE_FONT_SIZE = 10;
export const BUTTON_MENU_BUTTON_SCROLL_STEP = BUTTON_MENU_BUTTON_WIDTH + BUTTON_MENU_BUTTON_GAP_PX;
export const BUTTON_MENU_BUTTON_MOBILE_SCROLL_STEP =
  BUTTON_MENU_BUTTON_MOBILE_WIDTH + BUTTON_MENU_BUTTON_MOBILE_GAP_PX;

export const BUTTON_MENU_ROOT_SX: SxProps<Theme> = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  minWidth: 0,
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    gap: `${BUTTON_MENU_BUTTON_MOBILE_GAP_PX}px`,
  },
});

export const BUTTON_MENU_SCROLL_CONTAINER_SX: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  flex: '1 1 auto',
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
  py: 1,
  scrollbarWidth: 'none',
  WebkitOverflowScrolling: 'touch',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
};

export const BUTTON_MENU_SCROLL_ERROR_BUTTON_SX: SxProps<Theme> = {
  color: FORTA_ERROR,
  borderColor: FORTA_ERROR,
};

export const BUTTON_MENU_SCROLL_BUTTON_SX: SxProps<Theme> = {
  color: FORTA_BLUE.primary,
  backgroundColor: FORTA_GRAY1,
  border: `1px solid ${FORTA_BLUE.primary}`,
  width: 24,
  height: 24,
  p: 0.25,
  '&.Mui-disabled': {
    borderColor: FORTA_GRAY2,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
};

export const BUTTON_MENU_GROUP_SX: SxProps<Theme> = (theme) => ({
  display: 'inline-flex',
  columnGap: `${BUTTON_MENU_BUTTON_GAP_PX}px`,
  mx: 'auto',
  minWidth: 'max-content',
  [theme.breakpoints.down('sm')]: {
    columnGap: `${BUTTON_MENU_BUTTON_MOBILE_GAP_PX}px`,
  },
  '& .MuiButtonGroup-grouped': {
    borderRadius: 0,
    marginLeft: 0,
  },
});

export const BUTTON_MENU_STATUS_INDICATOR_SX: SxProps<Theme> = {
  display: 'block',
  width: BUTTON_MENU_STATUS_INDICATOR_WIDTH,
  height: BUTTON_MENU_STATUS_INDICATOR_HEIGHT,
  borderRadius: BUTTON_MENU_STATUS_INDICATOR_BORDER_RADIUS,
  flexShrink: 0,
  mt: 0.5,
  bgcolor: FORTA_BLUE.primary,
};

export const BUTTON_MENU_BUTTON_SX: SxProps<Theme> = (theme) => ({
  width: BUTTON_MENU_BUTTON_WIDTH,
  minWidth: BUTTON_MENU_BUTTON_WIDTH,
  maxWidth: BUTTON_MENU_BUTTON_WIDTH,
  fontSize: BUTTON_MENU_BUTTON_FONT_SIZE,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: 0,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  border: 'none',
  boxShadow: BUTTON_MENU_BUTTON_SHADOW,
  color: FORTA_BLUE.primary,
  p: BUTTON_MENU_BUTTON_PADDING,
  pb: 0.5,
  '&:hover': {
    border: 'none',
    boxShadow: BUTTON_MENU_BUTTON_HOVER_SHADOW,
    backgroundColor: theme.palette.action.hover,
  },
  '&&:hover': {
    border: 'none',
    boxShadow: BUTTON_MENU_BUTTON_HOVER_SHADOW,
  },
  '&.MuiButton-colorError': {
    border: 'none',
    color: FORTA_ERROR,
  },
  '&&.MuiButton-colorError:hover': {
    border: 'none',
  },
  [`& ${BUTTON_MENU_STATUS_INDICATOR_SELECTOR}`]: {
    ...BUTTON_MENU_STATUS_INDICATOR_SX,
  },
  [`&&[data-status="error"] ${BUTTON_MENU_STATUS_INDICATOR_SELECTOR}`]: {
    bgcolor: FORTA_ERROR,
  },
  [`&&[data-status="success"] ${BUTTON_MENU_STATUS_INDICATOR_SELECTOR}`]: {
    bgcolor: FORTA_SUCCESS,
  },
  [`&&[aria-current="page"] ${BUTTON_MENU_STATUS_INDICATOR_SELECTOR}`]: {
    bgcolor: FORTA_BLUE.light3,
  },
  [`&&[aria-current="page"][data-status="error"] ${BUTTON_MENU_STATUS_INDICATOR_SELECTOR}`]: {
    bgcolor: FORTA_ERROR,
  },
  [`&&[aria-current="page"][data-status="success"] ${BUTTON_MENU_STATUS_INDICATOR_SELECTOR}`]: {
    bgcolor: FORTA_SUCCESS,
  },
  '&&[aria-current="page"]': {
    backgroundColor: FORTA_BLUE.primary,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: FORTA_BLUE.primary,
    },
  },
  '&.MuiButton-containedError': {
    backgroundColor: FORTA_BLUE.primary,
    color: theme.palette.common.white,
  },
  [`&&.MuiButton-containedError ${BUTTON_MENU_STATUS_INDICATOR_SELECTOR}`]: {
    bgcolor: FORTA_ERROR,
  },
  [theme.breakpoints.down('sm')]: {
    width: BUTTON_MENU_BUTTON_MOBILE_WIDTH,
    minWidth: BUTTON_MENU_BUTTON_MOBILE_WIDTH,
    maxWidth: BUTTON_MENU_BUTTON_MOBILE_WIDTH,
    fontSize: BUTTON_MENU_BUTTON_MOBILE_FONT_SIZE,
    px: BUTTON_MENU_BUTTON_MOBILE_PADDING_X,
    py: BUTTON_MENU_BUTTON_MOBILE_PADDING_Y,
    [`& ${BUTTON_MENU_STATUS_INDICATOR_SELECTOR}`]: {
      width: BUTTON_MENU_STATUS_INDICATOR_MOBILE_WIDTH,
      height: BUTTON_MENU_STATUS_INDICATOR_MOBILE_HEIGHT,
      mt: 0.25,
    },
  },
});

export const BUTTON_MENU_ERROR_ICON_SX: SxProps<Theme> = {
  position: 'absolute',
  top: 3.2,
  right: 2,
  transform: 'translate(50%, -50%)',
  fontSize: 16,
  color: FORTA_ERROR,
};

export const BUTTON_MENU_SUCCESS_ICON_SX: SxProps<Theme> = {
  ...BUTTON_MENU_ERROR_ICON_SX,
  right: 4,
  color: FORTA_SUCCESS,
};
